import React, { useRef, useState } from "react";
import { Form, Button, Card, Col, Row } from "react-bootstrap";
import { motion } from "framer-motion";
import { MdOutlineRemoveCircleOutline } from "react-icons/md";
import { useEffect } from "react";
import Swal from "sweetalert2";
import QRCode from "react-qr-code";
import { authServices } from "../../APIServices/UserData/authServices";
import config from "../../config/config.json";
import { useApp } from "../../APIHelpers/AppContext";
import { decryptVaultKey, deriveMasterKey } from "../../APIHelpers/utils";
import { useNavigate } from "react-router-dom";

const LoginPage = ({
  nextPage,
  setNextPage,
  cardAnim,
  setEmail,
  name,
  setName,
  email,
  setLoader,
  loader,
  setUsePassword,
  setUseSecretKey,
  usePassword,
  useSecretKey,
}) => {
  const { setVaultKey, setAuth } = useApp();
  const navigate = useNavigate();
  const [accountList, setAccountList] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [password, setPassword] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [loginQRCode, setLoginQRCode] = useState("")
  const [changeLoginMode, setChangeLoginMode] = useState("qrMode")

  const checkRegistrationSetup = async () => {
    setLoader(true);
    let userData = {
      email,
    };
    let data = await authServices.checkRegistrationSetup(userData);
    if (data?.status) {
      setNextPage(data?.status);
      setUsePassword(data?.usePassword);
      setUseSecretKey(data?.useSecretKey);
      setEmail(data?.email);
      setName(data?.name || "");
    }
    setLoader(false);
  };
  const login = async () => {
    setLoader(true);
    let userData = {
      email,
      password,
      secretKey,
    };
    let data = await authServices.login(userData);
    if(data?.statusCode === 200){
      setAuth(data?.token)
      const masterKey = await deriveMasterKey(password, data.salt);
      const vaultKey = await decryptVaultKey(data.vaultKey, data.vaultKeyIV, masterKey);
      setVaultKey(vaultKey);
      navigate("/home");
    }
    setLoader(false);
  };

  const signUp = () => {
    window.location.href = "/signup";
  }

  function getTwoInitials(name) {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    const first = parts[0][0];
    const last = parts[parts.length - 1][0];
    return (first + last).toUpperCase();
  }

  function signInToAnotherAccount() {
    setEmail("");
    setName("");
    setUsePassword(false)
    setNextPage("startLogin")
  }

  function selectAccountForAccount(item) {
    setEmail(item?.email);
    setName(item?.name);
    setUsePassword(true)
    setNextPage("startLogin")
  }

  const handleRemove = (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to Remove ${item?.name} Account?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Remove it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const updatedList = accountList.filter(data => data?.email !== item?.email);
        setAccountList(updatedList);
        localStorage.setItem("accountList", JSON.stringify(updatedList));
      }
    });
  };

  const generateQRForWebLogin = async () => {
    let data = await authServices.generateQRForWebLogin()
    let deviceFingerprint = await authServices.getDeviceFingerprint();
    let result = {
      qrToken: data?.qrToken || "",
      deviceFingerprint: deviceFingerprint
    }
    let finalData = btoa(JSON.stringify(result))
    setLoginQRCode(finalData)
  }

  const qrWebLogin = async (token) => {
    let userData = {
      token: token
    }
    await authServices.qrWebLogin(userData)
  }

  const wsRef = useRef(null);

  useEffect(() => {
    if (wsRef.current) return;
    wsRef.current = new WebSocket(config?.WEBSOCKET_URL);
    const ws = wsRef.current;
    ws.onopen = () => { };
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "QR_APPROVED") {
        qrWebLogin(msg.value);
      }
    };
    ws.onerror = (err) => { };
    ws.onclose = () => { };
    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, []);

  useEffect(() => {
    generateQRForWebLogin()
    const interval = setInterval(() => {
      generateQRForWebLogin();
    }, 1 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const accountList = JSON.parse(localStorage.getItem("accountList"));
    setAccountList(accountList || [])
  }, []);

  useEffect(() => {
    setIsEdit(false)
  }, [nextPage]);

  return (
    <>
      <motion.div key="step0" {...cardAnim}>
        <Card className="border-0 shadow-lg" style={{ width: 420, borderRadius: "18px", background: "#ffffff" }}>
          {changeLoginMode === "emailMode" ? (
            <>
              {(nextPage === "loginProcess" && accountList?.length > 0) ? (
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <h4 className="fw-bold mb-1">Welcome Back</h4>
                    <div className={`hstack justify-content-between mt-4`}>
                      <small className="text-muted">
                        Choose an account
                      </small>
                      <small className="text-muted">
                        {isEdit ?
                          <span onClick={() => setIsEdit(false)} className="CP fw-bold text-danger">
                            Cancel
                          </span>
                          :
                          <span onClick={() => setIsEdit(true)} className="CP fw-bold text-primary">
                            Edit List
                          </span>
                        }
                      </small>
                    </div>
                  </div>
                  {accountList?.map((item, index) => (
                    <div className={`hstack justify-content-between`}>
                      <div
                        title="Select Account for Login"
                        onClick={() => selectAccountForAccount(item)}
                        key={index}
                        className="hstack gap-2 mb-3 CP"
                      >
                        <h6 className={`bg-primary-subtle p-3 rounded-1 fw-bold m-0`}>
                          {getTwoInitials(item?.name)}
                        </h6>
                        <div>
                          <h6 className="m-0">{item?.name}</h6>
                          <p className="small m-0 text-muted">{item?.email}</p>
                        </div>
                      </div>
                      {isEdit &&
                        <div>
                          <MdOutlineRemoveCircleOutline
                            onClick={() => handleRemove(item)}
                            className="text-danger CP"
                            title="Remove Account"
                          />
                        </div>
                      }
                    </div>
                  ))}
                  <div style={{ fontSize: 14 }} className={`hstack justify-content-between mt-4`}>
                    <small onClick={signInToAnotherAccount} className="CP fw-bold text-primary">
                      Sign in to another account
                    </small>
                    <small className="text-muted">
                      New user?{" "}
                      <span onClick={signUp} className="CP fw-bold text-primary">
                        Create an account
                      </span>
                    </small>
                  </div>
                </Card.Body>
              ) : (
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <h4 className="fw-bold mb-1">Welcome Back</h4>
                    <small className="text-muted">
                      Login to continue to Password Manager
                    </small>
                  </div>
                  {!usePassword ?
                    <Form.Group className="mb-3 floating-group">
                      <Form.Control
                        type="email"
                        placeholder=""
                        required
                        className="interactive-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <Form.Label>Email address</Form.Label>
                    </Form.Group>
                    : (
                      <>
                        <div className="hstack gap-2 mb-3">
                          <h6 className={`bg-primary-subtle p-3 rounded-1 fw-bold m-0`}>
                            {getTwoInitials(name)}
                          </h6>
                          <div>
                            <h6 className="m-0">{name}</h6>
                            <p className="small m-0 text-muted">{email}</p>
                          </div>
                        </div>
                        <Form.Group className="mb-3 floating-group">
                          <Form.Control
                            type="password"
                            required
                            placeholder=""
                            className="interactive-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <Form.Label>Password</Form.Label>
                        </Form.Group>
                      </>
                    )}
                  {useSecretKey && (
                    <Form.Group className="mb-3 floating-group">
                      <Form.Control
                        type="password"
                        required
                        placeholder=""
                        className="interactive-input"
                        value={secretKey}
                        onChange={(e) => setSecretKey(e.target.value)}
                      />
                      <Form.Label>Secret Key</Form.Label>
                    </Form.Group>
                  )}

                  <Form.Group className="hstack gap-2 mb-1">
                    <Form.Check type="checkbox" className="small" />
                    <small className="text-muted" style={{ fontSize: 12 }}>
                      Remember me
                    </small>
                  </Form.Group>

                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="mt-2"
                  >
                    {usePassword ? (
                      <Button
                        onClick={login}
                        className="w-100 py-2 fw-bold btn-gradient"
                        disabled={loader || (usePassword && !password) || (useSecretKey && !secretKey)}
                      >
                        Login
                      </Button>
                    ) : (
                      <Button
                        onClick={checkRegistrationSetup}
                        className="w-100 py-2 fw-bold btn-gradient"
                        disabled={loader || !email}
                      >
                        Continue
                      </Button>
                    )}
                  </motion.div>

                  <div style={{ fontSize: 14 }} className={`hstack justify-content-between text-end mt-2`}>
                    <small onClick={() => setChangeLoginMode("qrMode")} className="CP fw-bold text-primary">
                      Login with QR
                    </small>
                    <small className="text-muted">
                      New user?{" "}
                      <span onClick={signUp} className="CP fw-bold text-primary">
                        Create an account
                      </span>
                    </small>
                  </div>
                  <div style={{ fontSize: 14 }} className={`${accountList?.length > 0 ? "hstack justify-content-center" : ""} text-end mt-3`}>
                    {usePassword ?
                      <small onClick={signInToAnotherAccount} className="CP fw-bold text-primary">
                        Sign in to another account
                      </small>
                      : accountList?.length > 0 &&
                      <small onClick={() => setNextPage("loginProcess")} className="CP fw-bold text-primary">
                        Back to Account List
                      </small>
                    }
                  </div>
                </Card.Body>
              )}
            </>
          ) : changeLoginMode === "qrMode" && (
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <h4 className="fw-bold mb-1">Welcome Back</h4>
                <small className="text-muted">
                  Scan to continue to Password Manager
                </small>
              </div>
              <Row>
                <Form.Group as={Col} md={6} className="p-0">
                  <div className="hstack justify-content-center h-100">
                    {loginQRCode ? (
                      <QRCode className="border rounded-1 p-2" value={loginQRCode} size={160} />
                    ) : (
                      <QRCode className="border rounded-1 p-2" style={{ filter: "blur(5px) brightness(0.6)", userSelect: "none", pointerEvents: "none", transition: "filter 0.3s ease" }} value={loginQRCode} size={250} />
                    )}
                  </div>
                </Form.Group>
                <Form.Group as={Col} md={6} className="p-0">
                  <h6 className="small mb-1 fw-bold" style={{fontSize: 14}}>Use QR Code</h6>
                  <Form.Label className="small mb-1" style={{fontSize: 12}}>
                    <ul className="p-0 ps-3">
                      <li className="mt-2">Open Pixa Vault on a mobile device where you're signed in.</li>
                      <li className="mt-2">Select  the account icon or Pixa Voult logo in the top corner.</li>
                      <li className="mt-2">Choose <b>"Scan QR Code."</b>.</li>
                    </ul>
                  </Form.Label>
                </Form.Group>
              </Row>
              <div style={{ fontSize: 14 }} className={`hstack justify-content-between mt-4`}>
                <small onClick={() => setChangeLoginMode("emailMode")} className="CP fw-bold text-primary">
                  Login with Email
                </small>
                <small className="text-muted">
                  New user?{" "}
                  <span onClick={signUp} className="CP fw-bold text-primary">
                    Create an account
                  </span>
                </small>
              </div>
            </Card.Body>
          )}
        </Card>
      </motion.div>
    </>
  );
};

export default LoginPage;
